import { useParams } from "@tanstack/react-router";
import { CircleCheck, CircleDashed, Hammer, Plug } from "lucide-react";
import type { ReactNode } from "react";
import { Badge, Card, Skeleton, Spinner } from "@/design";
import {
  useActivity,
  useAgentTokens,
  useApproval,
  useDeployment,
  useDeploymentExplanation,
  useDeployments,
  useDomains,
  useEnvironments,
  useLogs,
  useManifestStatus,
  useMembers,
  useMetrics,
  useProject,
  useProjectGraph,
  useProjectSpend,
  useProjects,
  usePublicSafetySummary,
  useSafetyReport,
  useVariables,
  useWorkspace,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { config } from "@/lib/config";
import { formatMoney } from "@/lib/format";
import { useEnvName, useProjectSlug, useWorkspaceSlug } from "./context";
import { LogoMark } from "./Logo";
import { screens, type ScreenId } from "./ownership";
import { Page, PageHeader } from "./Page";

interface ProbeResult {
  isPending: boolean;
  error: unknown;
  summary?: ReactNode;
}

function probeOf(q: { isPending: boolean; error: unknown }, summary: () => ReactNode): ProbeResult {
  return { isPending: q.isPending, error: q.error, summary: q.isPending || q.error ? undefined : summary() };
}

type Probe = () => ProbeResult;

function useCtx() {
  const ws = useWorkspaceSlug();
  const project = useProjectSlug();
  const env = useEnvName();
  const params = useParams({ strict: false }) as { deploymentId?: string; approvalId?: string; domain?: string };
  return { ws, project, env, params };
}

/**
 * A tiny live check per screen that its main hook returns data, so workers start from something real.
 * Each entry is a hook; the placeholder is keyed by screen so the hook choice never changes while mounted.
 */
const probes: Record<ScreenId, Probe> = {
  "workspace-home": () => {
    const { ws } = useCtx();
    const q = useProjects(ws);
    return probeOf(q, () => `useProjects("${ws}") returned ${q.data?.data.length ?? 0} projects.`);
  },
  "workspace-settings": () => {
    const { ws } = useCtx();
    const q = useWorkspace(ws);
    return probeOf(q, () => `useWorkspace("${ws}"): ${q.data?.name}, ${q.data?.plan} plan, ${q.data?.member_count} members.`);
  },
  "new-project": () => {
    const { ws } = useCtx();
    const q = useProjects(ws);
    return probeOf(q, () => `Mock GitHub has 5 repos and stack detection ready. You have ${q.data?.data.length ?? 0} projects.`);
  },
  canvas: () => {
    const { project, env } = useCtx();
    const q = useProjectGraph(project, env);
    return probeOf(q, () => `useProjectGraph("${project}", "${env}"): ${q.data?.nodes.length} nodes, ${q.data?.edges.length} edges.`);
  },
  deployments: () => {
    const { project } = useCtx();
    const q = useDeployments(project);
    return probeOf(q, () => `useDeployments("${project}") returned ${q.data?.data.length} deploys; the latest is ${q.data?.data[0]?.status}.`);
  },
  "deployment-detail": () => {
    const { params } = useCtx();
    const q = useDeployment(params.deploymentId ?? "");
    const x = useDeploymentExplanation(params.deploymentId ?? "");
    return probeOf(q, () => `useDeployment: ${q.data?.service_name} is ${q.data?.status}.${x.data?.cause ? ` Explanation: “${x.data.cause}”` : ""}`);
  },
  logs: () => {
    const { project, env } = useCtx();
    const q = useLogs(project, { environment: env, limit: 50 });
    return probeOf(q, () => `useLogs("${project}") returned ${q.data?.lines.length} lines. useLogTail streams more over SSE.`);
  },
  metrics: () => {
    const { project, env } = useCtx();
    const q = useMetrics(project, { metric: ["cpu"], environment: env });
    return probeOf(q, () => `useMetrics("${project}", cpu) returned ${q.data?.series.length} series of ${q.data?.series[0]?.points.length ?? 0} points.`);
  },
  safety: () => {
    const { project } = useCtx();
    const q = useSafetyReport(project);
    return probeOf(q, () => `useSafetyReport("${project}"): grade ${q.data?.grade}, ${q.data?.counts?.fail ?? 0} failing, ${q.data?.counts?.warn ?? 0} warnings.`);
  },
  spend: () => {
    const { project } = useCtx();
    const q = useProjectSpend(project);
    return probeOf(q, () => q.data?.headline ?? `Month to date ${formatMoney(q.data?.month_to_date)}.`);
  },
  agents: () => {
    const { ws, project } = useCtx();
    const q = useAgentTokens(ws);
    const a = useActivity(ws, { project });
    return probeOf(q, () => `useAgentTokens("${ws}") returned ${q.data?.data.length} tokens; ${a.data?.data.length ?? 0} activity items for this project.`);
  },
  "settings-variables": () => {
    const { project, env } = useCtx();
    const q = useVariables(project, env);
    return probeOf(q, () => `useVariables("${project}", "${env}") returned ${q.data?.data.length} variables.`);
  },
  "settings-domains": () => useDomainsProbe(),
  "domain-wizard": () => useDomainsProbe(),
  "domain-detail": () => useDomainsProbe(),
  "settings-environments": () => {
    const { project } = useCtx();
    const q = useEnvironments(project);
    return probeOf(q, () => `useEnvironments("${project}"): ${q.data?.data.map((e) => e.name).join(", ")}.`);
  },
  "settings-members": () => {
    const { ws } = useCtx();
    const q = useMembers(ws);
    return probeOf(q, () => `useMembers("${ws}") returned ${q.data?.data.length} members.`);
  },
  "settings-manifest": () => {
    const { project } = useCtx();
    const q = useManifestStatus(project);
    return probeOf(q, () => `useManifestStatus("${project}"): ${q.data?.managed_by_manifest ? "managed by guv.yaml" : "not managed by guv.yaml"}${q.data?.drift?.detected ? ", drift detected" : ""}.`);
  },
  compliance: () => {
    const { project } = useCtx();
    const q = useProject(project);
    return probeOf(q, () => `useProject("${project}"): ${q.data?.classification} data, ${q.data?.residency}, region ${q.data?.region}.`);
  },
  approve: () => {
    const { params } = useCtx();
    const q = useApproval(params.approvalId ?? "");
    return probeOf(q, () => `useApproval: “${q.data?.action.summary}” (${q.data?.status}).`);
  },
  badge: () => {
    const { project } = useCtx();
    const q = usePublicSafetySummary(project);
    return probeOf(q, () => `usePublicSafetySummary("${project}"): grade ${q.data?.grade} for ${q.data?.project_name}.`);
  },
  login: () => ({ isPending: false, error: null, summary: "Mock passkey endpoints accept any assertion. POST /v1/auth/dev-login also works." }),
};

function useDomainsProbe(): ProbeResult {
  const { project, env } = useCtx();
  const q = useDomains(project, env);
  return probeOf(q, () => `useDomains("${project}", "${env}"): ${q.data?.data.map((d) => `${d.hostname} (${d.status})`).join(", ")}`);
}

function ProbeView({ screen }: { screen: ScreenId }) {
  const probe = probes[screen]();
  return (
    <div className={cn("mt-2 flex items-start gap-2 text-sm", probe.error ? "text-danger-text" : "text-text")}>
      {probe.isPending ? (
        <>
          <Spinner className="mt-0.5 size-3.5 text-muted" />
          <Skeleton className="h-4 w-48" />
        </>
      ) : probe.error ? (
        <span>{(probe.error as Error).message}</span>
      ) : (
        <>
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-ok" aria-hidden="true" />
          <span>{probe.summary}</span>
        </>
      )}
    </div>
  );
}

export interface ScreenPlaceholderProps {
  screen: ScreenId;
  /** Render outside the app shell (login, approve, badge). */
  standalone?: boolean;
}

/**
 * Tasteful stand-in for screens that are not built yet. It names the owner, lists what the screen will
 * do and proves the data layer works. Replace the route's component with the real screen.
 */
/** Builder metadata (owner, hook names, live probe) is for development only; real users never see it. */
const showBuildNotes = import.meta.env.DEV || config.mock;

export function ScreenPlaceholder({ screen, standalone }: ScreenPlaceholderProps) {
  const plan = screens[screen];
  const body = (
    <>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <Hammer className="size-3.5" /> {showBuildNotes ? "In progress" : "Coming soon"}
          </span>
        }
        title={plan.title}
        description={plan.summary}
        actions={
          showBuildNotes ? (
            <Badge tone="info" variant="soft" icon={<CircleDashed />}>
              Being built by {plan.owner.name}
            </Badge>
          ) : undefined
        }
      />
      <div className={cn("grid gap-5", showBuildNotes && "lg:grid-cols-[1.35fr_1fr]")}>
        <Card className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.5] [background-image:radial-gradient(var(--border-strong)_1px,transparent_1px)] [background-size:18px_18px] [mask-image:linear-gradient(to_bottom,black,transparent_70%)]"
          />
          <div className="relative p-6">
            <div className="text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">What this screen will do</div>
            <ul className="mt-4 space-y-3">
              {plan.features.map((f) => (
                <li key={f} className="flex gap-3 text-base text-text">
                  <CircleCheck className="mt-0.5 size-4 shrink-0 text-accent-text" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {showBuildNotes && (
              <div className="mt-6 border-t border-border pt-4 text-sm text-muted">
                Owner: <span className="font-medium text-text">{plan.owner.name}</span> · {plan.owner.area}
              </div>
            )}
          </div>
        </Card>
        {showBuildNotes && (
        <Card className="flex flex-col">
          <div className="p-6">
            <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">
              <Plug className="size-3.5" /> Data it uses
            </div>
            <ul className="mt-4 space-y-1.5">
              {plan.hooks.map((h) => (
                <li key={h}>
                  <code className="block truncate rounded-md bg-surface-2 px-2.5 py-1.5 font-mono text-xs text-text" title={h}>
                    {h}
                  </code>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-auto border-t border-border bg-surface-2/50 px-6 py-4">
            <div className="text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">Live check</div>
            <ProbeView key={screen} screen={screen} />
          </div>
        </Card>
        )}
      </div>
    </>
  );
  if (!standalone) return <Page>{body}</Page>;
  return (
    <div className="min-h-dvh">
      <header className="flex h-14 items-center border-b border-border px-6">
        <span className="inline-flex items-center gap-2">
          <LogoMark />
          <span className="font-display text-xl font-semibold">Guv</span>
        </span>
      </header>
      <Page width="default">{body}</Page>
    </div>
  );
}
